import {
  type AdminBotProfile,
  type AdminBotProfileRequest,
  botProfileLimits,
} from "@ternary/contracts"
import { Alert, Button, Card, Field, Input, Select, Textarea } from "@ternary/ui"
import { type ReactElement, useId, useState } from "react"
import { ImageUploadField } from "./image-upload-field.js"

const DATE = new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" })

/** The re-add link that also asks Discord for Change Nickname. */
export function changeNicknameLink(reinstallUrl: string) {
  return `${reinstallUrl}${reinstallUrl.includes("?") ? "&" : "?"}extra=changeNickname`
}

/**
 * The bot's profile in one of the business's servers (Connections → Discord → Server): its
 * nickname, bio, avatar and banner, with a preview. Save keeps the draft; nothing changes in
 * Discord until "Apply to <server>". "Reset to Ternary" puts Discord's defaults back. Without
 * Change Nickname the card says what to give the Ternary role, with a re-add link. It only shows:
 * the page saves through `onSave`, `onApply` and `onReset`.
 */
export function BotProfileCard({
  busy,
  guildId,
  guilds,
  onApply,
  onGuildChange,
  onReset,
  onSave,
  profile,
  writable,
}: {
  /** Which action is on its way, if any. */
  busy: "save" | "apply" | "reset" | null
  guildId: string
  /** The business's servers, for their names, the picker and the re-add link. */
  guilds: ReadonlyArray<{ id: string; name: string; reinstallUrl?: string | undefined }>
  onApply(draft: AdminBotProfileRequest): void
  onGuildChange(guildId: string): void
  onReset(): void
  onSave(draft: AdminBotProfileRequest): void
  profile: AdminBotProfile
  writable: boolean
}): ReactElement {
  const formId = useId()
  const [draft, setDraft] = useState<AdminBotProfileRequest>(profile.draft)
  const [images, setImages] = useState(profile.images)
  const edit = (change: Partial<AdminBotProfileRequest>) => setDraft({ ...draft, ...change })
  const guild = guilds.find(({ id }) => id === guildId)
  const server = guild?.name || "your server"
  // Characters, as Discord counts them: an emoji is one.
  const length = (value: string | null) => Array.from((value ?? "").trim()).length
  const nickTooLong = length(draft.nick) > botProfileLimits.nick
  const bioTooLong = length(draft.bio) > botProfileLimits.bio
  const avatarUrl =
    draft.avatar === "logo" ? images.logo : draft.avatar === "custom" ? images.customAvatar : null
  const cleaned = (): AdminBotProfileRequest => ({
    ...draft,
    bio: draft.bio?.trim() || null,
    nick: draft.nick?.trim() || null,
  })
  const ready = !nickTooLong && !bioTooLong && busy === null

  return (
    <Card
      actions={
        writable ? (
          <Button
            disabled={busy !== null}
            loading={busy === "reset"}
            onClick={onReset}
            size="sm"
            variant="secondary"
          >
            Reset to Ternary
          </Button>
        ) : undefined
      }
      title="Bot profile"
    >
      <form
        aria-label="Bot profile"
        className="admin-stack admin-stack-tight"
        id={formId}
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          if (ready) onSave(cleaned())
        }}
      >
        {guilds.length > 1 ? (
          <Field label="Server">
            <Select onChange={(event) => onGuildChange(event.target.value)} value={guildId}>
              {guilds.map(({ id, name }) => (
                <option key={id} value={id}>
                  {name || id}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <figure aria-label="Preview" className="admin-bot-preview">
          <span className="admin-upload-tile">
            {avatarUrl ? <img alt="" src={avatarUrl} /> : <span aria-hidden="true">T</span>}
          </span>
          <figcaption>
            <strong>{draft.nick?.trim() || "Ternary"}</strong>
            <span className="admin-note" role="status">
              {profile.applied
                ? `Applied in ${server} ${DATE.format(new Date(profile.applied.at))}`
                : `Not applied in ${server} yet: nothing changes in Discord until you apply`}
            </span>
          </figcaption>
        </figure>
        {profile.lastError?.code === 50001 ? (
          <Alert
            action={
              writable && guild?.reinstallUrl ? (
                <a
                  className="tny-button tny-button-secondary admin-button-link"
                  href={guild.reinstallUrl}
                >
                  Re-add the bot
                </a>
              ) : undefined
            }
            tone="warning"
          >
            {`The bot isn't in ${server}. Add it again, then apply.`}
          </Alert>
        ) : null}
        {profile.live && !profile.canChangeNickname ? (
          <Alert
            action={
              writable && guild?.reinstallUrl ? (
                <a
                  className="tny-button tny-button-secondary admin-button-link"
                  href={changeNicknameLink(guild.reinstallUrl)}
                >
                  Re-add the bot
                </a>
              ) : undefined
            }
            tone="warning"
          >
            {`The bot can't change its nickname in ${server}. Give the Ternary role Change Nickname, or re-add the bot.`}
          </Alert>
        ) : null}
        <fieldset className="admin-fieldset admin-columns" disabled={!writable}>
          <Field
            error={nickTooLong ? `Use up to ${botProfileLimits.nick} characters.` : undefined}
            label="Nickname"
          >
            <Input
              autoComplete="off"
              onChange={(event) => edit({ nick: event.target.value })}
              placeholder="Ternary"
              value={draft.nick ?? ""}
            />
          </Field>
          <Field
            error={bioTooLong ? `Use up to ${botProfileLimits.bio} characters.` : undefined}
            label="Bio"
          >
            <Textarea
              onChange={(event) => edit({ bio: event.target.value })}
              rows={2}
              value={draft.bio ?? ""}
            />
          </Field>
        </fieldset>
        {/* Two short choices side by side, on a phone too. */}
        <fieldset className="admin-fieldset admin-bot-images" disabled={!writable}>
          <Field label="Avatar">
            <Select
              onChange={(event) =>
                edit({ avatar: event.target.value as AdminBotProfileRequest["avatar"] })
              }
              value={draft.avatar}
            >
              <option disabled={!images.logo} value="logo">
                Your logo
              </option>
              <option value="custom">Another image</option>
              <option value="none">Discord's default</option>
            </Select>
          </Field>
          <Field label="Banner">
            <Select
              onChange={(event) =>
                edit({ banner: event.target.value as AdminBotProfileRequest["banner"] })
              }
              value={draft.banner}
            >
              <option disabled={!images.background} value="background">
                Your background
              </option>
              <option value="custom">Another image</option>
              <option value="none">None</option>
            </Select>
          </Field>
        </fieldset>
        {writable && draft.avatar === "custom" ? (
          <ImageUploadField
            kind="bot-avatar"
            label="Bot avatar"
            // From the current images: another upload may have landed since this one began.
            onChange={(url) => setImages((current) => ({ ...current, customAvatar: url }))}
            value={images.customAvatar}
          />
        ) : null}
        {writable && draft.banner === "custom" ? (
          <ImageUploadField
            kind="bot-banner"
            label="Bot banner"
            onChange={(url) => setImages((current) => ({ ...current, customBanner: url }))}
            value={images.customBanner}
          />
        ) : null}
        {writable ? (
          <div className="admin-row">
            <Button disabled={!ready} loading={busy === "save"} type="submit" variant="secondary">
              Save
            </Button>
            <Button disabled={!ready} loading={busy === "apply"} onClick={() => onApply(cleaned())}>
              {`Apply to ${server}`}
            </Button>
          </div>
        ) : (
          <p className="admin-note">Only owners and admins can change this.</p>
        )}
      </form>
    </Card>
  )
}
